/* eslint-disable @typescript-eslint/no-floating-promises */
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { environment } from 'environments/environment';
import { interval } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

@Injectable()
export class SwUpdateNotifierService {
	constructor(private readonly _swUpdate: SwUpdate, private readonly _matSnackBar: MatSnackBar) {}

	init(): void {
		// Check if service worker is enabled
		if (!this._swUpdate.isEnabled) {
			console.log('Service Worker not enabled');
			return;
		}

		this._swUpdate.versionUpdates
			.pipe(
				tap((evt) => {
					if (evt.type === 'VERSION_DETECTED') {
						console.log(`Downloading new app version: ${evt.version.hash}`);
					} else if (evt.type === 'VERSION_INSTALLATION_FAILED') {
						console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
					} else if (evt.type === 'VERSION_READY') {
						console.log(`Current app version is: ${evt.currentVersion.hash}`);
						console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
					} else if (evt.type === 'NO_NEW_VERSION_DETECTED') {
						console.log(`No new app version detected`);
					}
				}),
				filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
			)
			.subscribe(() => {
				this._showUpdateNotification();
			});

		// Check for updates periodically in production
		if (environment.enableServiceWorker) {
			interval(60000).subscribe(() => {
				// Increased to 1 minute to reduce server load
				void this._swUpdate.checkForUpdate();
			});
		}

		// Check for update immediately
		void this._swUpdate.checkForUpdate();
	}

	private _showUpdateNotification(): void {
		const ref = this._matSnackBar.open('Nueva versión disponible', 'Actualizar', {
			duration: 0, // Keep open until action is taken
			panelClass: 'update-snackbar',
		});

		ref.onAction().subscribe(() => {
			this._applyUpdate();
		});
	}

	private async _applyUpdate(): Promise<void> {
		try {
			// Clear all caches before activating update
			await this._clearAllCaches();

			// Activate the update
			await this._swUpdate.activateUpdate();

			// Reload the page
			window.location.reload();
		} catch (error) {
			console.error('Error applying update:', error);
			// Fallback: force reload
			window.location.reload();
		}
	}

	private async _clearAllCaches(): Promise<void> {
		try {
			const cacheNames = await caches.keys();
			console.log('Available caches:', cacheNames);

			// Delete all caches
			await Promise.all(
				cacheNames.map((cacheName) => {
					console.log('Deleting cache:', cacheName);
					return caches.delete(cacheName);
				})
			);

			console.log('All caches cleared');
		} catch (error) {
			console.error('Error clearing caches:', error);
		}
	}
}
